import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useTokenPayloadStore } from '@nsftx/seven-client-retail-vue-sgp'
import { calculateCashFlow, countExcludedEntries } from '@/modules/calculator/calculatorService'
import {
  calculatorSessionHasActivity,
  createEmptyCalculatorSession,
  getCalculatorSessionEntryCount,
} from '@/modules/calculator/calculatorSessionService'
import type { CalculatorCurrencySession, CalculatorEntry } from '@/modules/calculator/types'

type CalculatorEntryInput = CalculatorEntry & { currency: string }

export const useCalculatorStore = defineStore('calculatorStore', () => {
  const sessionsByCurrency = ref<Record<string, CalculatorCurrencySession>>({})
  const isResetModalOpen = ref(false)
  const isMoneyInModalOpen = ref(false)
  const isCompact = ref(false)

  const { baseCurrency } = storeToRefs(useTokenPayloadStore())
  const supportedCurrencies = computed(() => [baseCurrency.value || 'EUR'])

  const selectedCurrency = ref<string | null>(null)

  const currency = computed({
    get: () => selectedCurrency.value ?? baseCurrency.value ?? 'EUR',
    set: (value: string) => {
      selectedCurrency.value = value
    },
  })

  const ensureSession = (currencyCode: string): CalculatorCurrencySession => {
    if (!sessionsByCurrency.value[currencyCode]) {
      sessionsByCurrency.value[currencyCode] = createEmptyCalculatorSession()
    }

    return sessionsByCurrency.value[currencyCode]
  }

  const touchSessions = () => {
    sessionsByCurrency.value = { ...sessionsByCurrency.value }
  }

  const activeSession = computed(() => ensureSession(currency.value))

  const moneyIn = computed({
    get: () => activeSession.value.moneyIn,
    set: (value: number | null) => {
      activeSession.value.moneyIn = value
      touchSessions()
    },
  })

  const entries = computed(() => activeSession.value.entries)

  const cashFlow = computed(() => calculateCashFlow(moneyIn.value || 0, entries.value))

  const transactionCount = computed(() => getCalculatorSessionEntryCount(activeSession.value))

  const excludedTransactionCount = computed(() => countExcludedEntries(entries.value))

  const hasExcludedTransactions = computed(
    () => excludedTransactionCount.value > 0 && transactionCount.value > 0,
  )

  const hasSessionActivity = computed(() => calculatorSessionHasActivity(activeSession.value))

  const currencyTransactionCounts = computed(() => {
    const counts: Record<string, number> = {}

    for (const currencyCode of supportedCurrencies.value ?? []) {
      counts[currencyCode] = getCalculatorSessionEntryCount(ensureSession(currencyCode))
    }

    return counts
  })

  const currencyOptions = computed(() =>
    (supportedCurrencies.value ?? []).map((value) => ({
      label: `${value} (${currencyTransactionCounts.value[value] ?? 0})`,
      value,
    })),
  )

  const setMoneyIn = (amount: number) => {
    moneyIn.value = Number.isFinite(amount) && amount >= 0 ? amount : 0
  }

  const addEntry = ({ currency: currencyCode, ...entry }: CalculatorEntryInput) => {
    const session = ensureSession(currencyCode)
    const isDuplicate = session.entries.some(
      ({ transactionId, action }) =>
        transactionId === entry.transactionId && action === entry.action,
    )

    if (isDuplicate) {
      return
    }

    session.entries.unshift(entry)
    touchSessions()
  }

  const updateEntryStatus = (entryId: string, status: CalculatorEntry['status']) => {
    const entry = entries.value.find(({ id }) => id === entryId)

    if (!entry) {
      return
    }

    entry.status = status
  }

  const setEntryIncluded = (entryId: string, included: boolean) => {
    const entry = entries.value.find(({ id }) => id === entryId)

    if (!entry) {
      return
    }

    entry.included = included
  }

  const openResetModal = () => {
    isResetModalOpen.value = true
  }

  const closeResetModal = () => {
    isResetModalOpen.value = false
  }

  const openMoneyInModal = () => {
    isMoneyInModalOpen.value = true
  }

  const closeMoneyInModal = () => {
    isMoneyInModalOpen.value = false
  }

  const toggleCompact = () => {
    isCompact.value = !isCompact.value
  }

  const clearCurrencySession = (currencyCode: string) => {
    sessionsByCurrency.value[currencyCode] = createEmptyCalculatorSession()
    touchSessions()
  }

  const clearActiveCurrencySession = () => {
    clearCurrencySession(currency.value)
    isResetModalOpen.value = false
    isMoneyInModalOpen.value = false
  }

  const confirmReset = () => {
    clearActiveCurrencySession()
  }

  return {
    sessionsByCurrency,
    moneyIn,
    entries,
    isResetModalOpen,
    isMoneyInModalOpen,
    isCompact,
    currency,
    cashFlow,
    transactionCount,
    currencyTransactionCounts,
    currencyOptions,
    excludedTransactionCount,
    hasExcludedTransactions,
    hasSessionActivity,
    setMoneyIn,
    addEntry,
    updateEntryStatus,
    setEntryIncluded,
    openResetModal,
    closeResetModal,
    openMoneyInModal,
    closeMoneyInModal,
    toggleCompact,
    clearCurrencySession,
    clearActiveCurrencySession,
    confirmReset,
    supportedCurrencies,
    ensureSession,
  }
})
